/**
 * The CRM module's route table (CLAUDE.md §8.11 — the lead-to-opportunity and
 * service-case half; `internal/sales` owns Quote and Sales Order).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `CRM_ROUTES` in. Keeping them here means the
 * paths, the permissions and the components that implement them are decided in
 * one place by whoever owns the module.
 *
 * Two things here are load-bearing:
 *
 *  - Every `:id` is constrained to `\d+`, so a literal segment can never be
 *    parsed as a record id. There is no `/new` screen in this module — a lead, a
 *    case and an article are all raised through a dialog on their list, because
 *    each is a short form and none of them has a draft phase worth a URL.
 *  - `/cases/:id` is an **alias** of the case screen, not a second route.
 *    `internal/crm/sla.go`'s `caseLink` builds `/cases/%d` for the bell
 *    notification an SLA breach raises, while every other module in this app is
 *    mounted under its own prefix. An alias resolves the server's link without
 *    a redirect hop and without giving the screen two canonical URLs — see the
 *    note in the report; the mismatch is the server's spelling, not this app's.
 *
 * `CRM_PATHS` is the same set as strings, for links inside the module. Links
 * *out* of it — the `internal/ar` customer a converted lead became — are built
 * and then checked against the router by `linkIfRoutable` before being
 * rendered: AR owns that URL, and a guessed link that 404s in the middle of the
 * one trail proving a conversion happened is worse than plain text.
 */

import type { Router, RouteRecordRaw } from 'vue-router'

import { CRM_PERMISSIONS } from '@/modules/crm/permissions'

export const CRM_ROUTES: RouteRecordRaw[] = [
  {
    path: '/crm/leads',
    name: 'crm-leads',
    component: () => import('@/modules/crm/LeadsListView.vue'),
    meta: { titleKey: 'crm.routeTitles.leads', permission: CRM_PERMISSIONS.leadRead },
  },
  {
    path: '/crm/leads/:id(\\d+)',
    name: 'crm-lead',
    component: () => import('@/modules/crm/LeadView.vue'),
    meta: { titleKey: 'crm.routeTitles.lead', permission: CRM_PERMISSIONS.leadRead },
  },

  {
    path: '/crm/opportunities',
    name: 'crm-opportunities',
    component: () => import('@/modules/crm/OpportunitiesListView.vue'),
    meta: {
      titleKey: 'crm.routeTitles.opportunities',
      permission: CRM_PERMISSIONS.opportunityRead,
    },
  },
  {
    path: '/crm/opportunities/:id(\\d+)',
    name: 'crm-opportunity',
    component: () => import('@/modules/crm/OpportunityView.vue'),
    meta: {
      titleKey: 'crm.routeTitles.opportunity',
      permission: CRM_PERMISSIONS.opportunityRead,
    },
  },

  {
    path: '/crm/cases',
    name: 'crm-cases',
    component: () => import('@/modules/crm/CasesListView.vue'),
    meta: { titleKey: 'crm.routeTitles.cases', permission: CRM_PERMISSIONS.caseRead },
  },
  {
    path: '/crm/cases/:id(\\d+)',
    // `internal/crm/sla.go` links a breach notification at `/cases/:id`.
    alias: '/cases/:id(\\d+)',
    name: 'crm-case',
    component: () => import('@/modules/crm/CaseView.vue'),
    meta: { titleKey: 'crm.routeTitles.case', permission: CRM_PERMISSIONS.caseRead },
  },
  {
    path: '/crm/sla-policies',
    name: 'crm-sla-policies',
    component: () => import('@/modules/crm/SlaPoliciesView.vue'),
    meta: { titleKey: 'crm.routeTitles.slaPolicies', permission: CRM_PERMISSIONS.caseRead },
  },

  {
    path: '/crm/kb',
    name: 'crm-kb-articles',
    component: () => import('@/modules/crm/KbListView.vue'),
    meta: { titleKey: 'crm.routeTitles.kbArticles', permission: CRM_PERMISSIONS.kbRead },
  },
  {
    path: '/crm/kb/:id(\\d+)',
    name: 'crm-kb-article',
    component: () => import('@/modules/crm/KbArticleView.vue'),
    meta: { titleKey: 'crm.routeTitles.kbArticle', permission: CRM_PERMISSIONS.kbRead },
  },

  {
    path: '/crm/reports/pipeline',
    name: 'crm-pipeline-report',
    component: () => import('@/modules/crm/PipelineReportView.vue'),
    meta: { titleKey: 'crm.routeTitles.pipelineReport', permission: CRM_PERMISSIONS.reportRead },
  },
  {
    path: '/crm/reports/conversion',
    name: 'crm-conversion-report',
    component: () => import('@/modules/crm/ConversionReportView.vue'),
    meta: { titleKey: 'crm.routeTitles.conversionReport', permission: CRM_PERMISSIONS.reportRead },
  },
  {
    path: '/crm/reports/service',
    name: 'crm-service-report',
    component: () => import('@/modules/crm/ServiceReportView.vue'),
    meta: { titleKey: 'crm.routeTitles.serviceReport', permission: CRM_PERMISSIONS.reportRead },
  },
]

/**
 * The C5 CRM route table — accounts, contacts, segments, campaigns, forecast
 * depth and lead scoring.
 *
 * Exported **separately** from `CRM_ROUTES`, and the separation is deliberate
 * rather than tidiness.
 *
 * `CRM_ROUTES` is already spread into `src/router/index.ts`, and
 * `src/nav/keys.test.ts` walks that router asserting every `titleKey` resolves
 * in the **merged** catalogue. A module's locale fragment only reaches that
 * catalogue when `scripts/merge-module-locales.py` runs, which is the shared
 * wiring packet's step — so folding these records into `CRM_ROUTES` would put
 * fifteen unresolved keys into the shared router and turn a green test red for
 * the whole build, for an ordering nobody chose on purpose.
 *
 * So they wait here, exactly as `PARTY_ROUTES` does, and the wiring packet
 * spreads both in the same edit it merges the fragments. One import and one
 * spread, and no judgement about a module it did not build.
 *
 * `/crm-accounts` is the API path because `/accounts` is `internal/org`'s chart
 * of accounts; the *screen* path is `/crm/accounts`, which is unambiguous inside
 * this app because everything under `/crm` is CRM's. Each `:id` is
 * `\d+`-constrained, and every literal segment sharing a prefix with one is
 * declared before it.
 */
export const CRM_C5_ROUTES: RouteRecordRaw[] = [
  {
    path: '/crm/accounts',
    name: 'crm-accounts',
    component: () => import('@/modules/crm/c5/accounts/AccountsListView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.accounts', permission: CRM_PERMISSIONS.accountRead },
  },
  {
    path: '/crm/accounts/catalogues',
    name: 'crm-account-catalogues',
    component: () => import('@/modules/crm/c5/accounts/AccountCataloguesView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.catalogues', permission: CRM_PERMISSIONS.accountRead },
  },
  {
    path: '/crm/accounts/:id(\\d+)',
    name: 'crm-account',
    component: () => import('@/modules/crm/c5/accounts/AccountView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.account', permission: CRM_PERMISSIONS.accountRead },
  },
  {
    path: '/crm/contacts',
    name: 'crm-contacts',
    component: () => import('@/modules/crm/c5/contacts/ContactsListView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.contacts', permission: CRM_PERMISSIONS.accountRead },
  },
  {
    path: '/crm/contacts/:id(\\d+)',
    name: 'crm-contact',
    component: () => import('@/modules/crm/c5/contacts/ContactView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.contact', permission: CRM_PERMISSIONS.accountRead },
  },
  {
    path: '/crm/segments',
    name: 'crm-segments',
    component: () => import('@/modules/crm/c5/segments/SegmentsListView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.segments', permission: CRM_PERMISSIONS.campaignRead },
  },
  {
    path: '/crm/segments/:id(\\d+)',
    name: 'crm-segment',
    component: () => import('@/modules/crm/c5/segments/SegmentBuilderView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.segment', permission: CRM_PERMISSIONS.campaignRead },
  },
  {
    path: '/crm/campaigns',
    name: 'crm-campaigns',
    component: () => import('@/modules/crm/c5/campaigns/CampaignsListView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.campaigns', permission: CRM_PERMISSIONS.campaignRead },
  },
  {
    path: '/crm/campaigns/:id(\\d+)',
    name: 'crm-campaign',
    component: () => import('@/modules/crm/c5/campaigns/CampaignView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.campaign', permission: CRM_PERMISSIONS.campaignRead },
  },
  {
    path: '/crm/campaigns/:id(\\d+)/send',
    name: 'crm-campaign-send',
    component: () => import('@/modules/crm/c5/campaigns/CampaignSendView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.send', permission: CRM_PERMISSIONS.campaignRead },
  },
  {
    path: '/crm/campaign-sends/:id(\\d+)',
    name: 'crm-campaign-send-detail',
    component: () => import('@/modules/crm/c5/campaigns/CampaignSendDetailView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.sendDetail', permission: CRM_PERMISSIONS.campaignRead },
  },
  {
    path: '/crm/forecast',
    name: 'crm-forecast-board',
    component: () => import('@/modules/crm/c5/forecast/ForecastBoardView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.forecast', permission: CRM_PERMISSIONS.reportRead },
  },
  {
    path: '/crm/win-loss',
    name: 'crm-win-loss',
    component: () => import('@/modules/crm/c5/forecast/WinLossView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.winLoss', permission: CRM_PERMISSIONS.reportRead },
  },
  {
    path: '/crm/score-models',
    name: 'crm-score-models',
    component: () => import('@/modules/crm/c5/scoring/ScoreModelsView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.scoring', permission: CRM_PERMISSIONS.leadRead },
  },
  {
    path: '/crm/routing-rules',
    name: 'crm-routing-rules',
    component: () => import('@/modules/crm/c5/scoring/RoutingRulesView.vue'),
    meta: { titleKey: 'crm.c5.routeTitles.routing', permission: CRM_PERMISSIONS.leadRead },
  },
]

/* ------------------------------------------------------------ own screens */

export const CRM_PATHS = {
  leads: '/crm/leads',
  lead: (id: number) => `/crm/leads/${id}`,
  opportunities: '/crm/opportunities',
  opportunity: (id: number) => `/crm/opportunities/${id}`,
  cases: '/crm/cases',
  case: (id: number) => `/crm/cases/${id}`,
  slaPolicies: '/crm/sla-policies',
  kbArticles: '/crm/kb',
  kbArticle: (id: number) => `/crm/kb/${id}`,
  pipelineReport: '/crm/reports/pipeline',
  conversionReport: '/crm/reports/conversion',
  serviceReport: '/crm/reports/service',
} as const

/** The open cases of one customer — the hop from a case to its customer's others. */
export function casesForCustomer(customerId: number): string {
  return `${CRM_PATHS.cases}?customerId=${customerId}`
}

/** Leads matching a customer's email — the only backward hop the API supports. */
export function leadsMatching(term: string): string {
  return `${CRM_PATHS.leads}?q=${encodeURIComponent(term)}`
}

/* -------------------------------------------------------- links elsewhere */

/** The `internal/ar` customer a converted lead became. AR owns this URL. */
export function customerRoute(customerId: number): string {
  return `/ar/customers/${customerId}`
}

/**
 * The path, but only if this app actually has a screen for it.
 *
 * The router's catch-all matches everything, so "resolves" is not the question —
 * "resolves to something other than the not-found page" is. A path the router
 * would send to the 404 screen comes back as null and the caller renders text.
 */
export function linkIfRoutable(router: Router, path: string | null): string | null {
  if (path === null || path === '') return null
  try {
    const resolved = router.resolve(path)
    if (resolved.matched.length === 0) return null
    if (resolved.matched.every((record) => record.name === 'not-found')) return null
    return path
  } catch {
    // An unparseable path is not a link either.
    return null
  }
}
